import { useEffect, useRef, useState } from "react";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { CalendarCheck, MapPin, User } from "lucide-react";
import { fadeUp, revealOnce } from "../lib/motion";

// ============================================================================
// MobileAppsComingSoon — cinematic scroll-driven 3D product-teaser section.
// Desktop: sticky 100svh stage inside a tall scroll track; phones start behind
// the headline (translateZ), glide toward the viewer, counter-rotate, a
// restrained red light sweeps through, then the phones separate to reveal the
// finale line. Everything is GPU-friendly transforms/opacity via framer-motion
// (passive scroll + rAF internally — no manual scroll handlers, no WebGL).
// Phone screens are CSS recreations of REAL product surfaces only (map search,
// arrival-code verification) — no fabricated listings, prices, or fake data.
// prefers-reduced-motion: static composition, no scroll timeline.
// ============================================================================

// Depth/rotation presets per breakpoint — desktop full, tablet reduced,
// mobile simplified (and the stage itself CSS-scales down to fit).
const PRESETS = {
  desktop: { zBack: -300, zFwd: 90, rotIn: 14, rotMid: 4, rotOut: -10, sep: 290, screenDrift: 26 },
  tablet: { zBack: -180, zFwd: 50, rotIn: 9, rotMid: 3, rotOut: -6, sep: 175, screenDrift: 16 },
  mobile: { zBack: -90, zFwd: 30, rotIn: 5, rotMid: 1.5, rotOut: -3, sep: 120, screenDrift: 10 },
};

function usePreset() {
  const [preset, setPreset] = useState("desktop");
  useEffect(() => {
    const pick = () => setPreset(window.innerWidth < 640 ? "mobile" : window.innerWidth < 1024 ? "tablet" : "desktop");
    pick();
    window.addEventListener("resize", pick);
    return () => window.removeEventListener("resize", pick);
  }, []);
  return PRESETS[preset];
}

/* ---------- App screen mockups (real product surfaces, no fake data) ---------- */

const MapGrid = () => (
  <div className="absolute inset-0" aria-hidden="true">
    {/* street grid */}
    {[...Array(6)].map((_, i) => (
      <div key={`h${i}`} className="absolute left-0 right-0 h-px bg-white/[0.05]" style={{ top: `${14 + i * 16}%` }} />
    ))}
    {[...Array(5)].map((_, i) => (
      <div key={`v${i}`} className="absolute top-0 bottom-0 w-px bg-white/[0.05]" style={{ left: `${12 + i * 20}%` }} />
    ))}
    {/* arterials */}
    <div className="absolute left-[-20%] right-[-20%] top-[38%] h-[5px] bg-white/[0.08] rotate-[-8deg] rounded-full" />
    <div className="absolute left-[-20%] right-[-20%] top-[64%] h-[4px] bg-white/[0.07] rotate-[6deg] rounded-full" />
    <div className="absolute top-[-10%] bottom-[-10%] left-[58%] w-[5px] bg-white/[0.08] rotate-[10deg] rounded-full" />
    {/* parking pins — generic markers, not listings */}
    <MapPin className="absolute w-4 h-4 text-[#E50914]" style={{ left: "30%", top: "30%" }} fill="#E50914" />
    <MapPin className="absolute w-4 h-4 text-[#E50914]/70" style={{ left: "66%", top: "52%" }} fill="#E50914" />
    <MapPin className="absolute w-3.5 h-3.5 text-[#E50914]/50" style={{ left: "46%", top: "68%" }} fill="#E50914" />
  </div>
);

const BottomNav = ({ active = 0 }) => {
  const items = [
    { icon: MapPin, label: "Find" },
    { icon: CalendarCheck, label: "Bookings" },
    { icon: User, label: "Profile" },
  ];
  return (
    <div className="absolute bottom-0 inset-x-0 h-12 border-t border-white/[0.07] bg-[#0D1114]/95 backdrop-blur flex items-center justify-around px-4">
      {items.map((it, i) => (
        <div key={it.label} className={`flex flex-col items-center gap-0.5 ${i === active ? "text-[#FF3B45]" : "text-zinc-600"}`}>
          <it.icon className="w-3.5 h-3.5" />
          <span className="text-[0.45rem] font-medium tracking-wide">{it.label}</span>
        </div>
      ))}
    </div>
  );
};

// iOS — the "Find parking" map surface (real feature).
const IosScreen = ({ drift }) => (
  <motion.div style={{ y: drift }} className="absolute inset-0 bg-[#0B0E11] overflow-hidden will-change-transform">
    <MapGrid />
    {/* search pill — same placeholder as the live product */}
    <div className="absolute top-9 inset-x-3 h-8 rounded-full bg-[#16191D]/95 border border-white/10 flex items-center gap-1.5 px-3 shadow-lg">
      <MapPin className="w-3 h-3 text-[#FF3B45] shrink-0" />
      <span className="text-[0.5rem] text-zinc-500 truncate">City, address, or landmark</span>
    </div>
    {/* bottom sheet — skeleton rows while real data loads */}
    <div className="absolute bottom-12 inset-x-0 rounded-t-2xl bg-[#101316] border-t border-white/[0.08] px-3.5 pt-2.5 pb-3">
      <div className="w-8 h-1 rounded-full bg-white/15 mx-auto mb-2.5" />
      <p className="text-[0.55rem] font-semibold text-white mb-2">Nearby parking</p>
      <div className="space-y-1.5">
        <div className="h-7 rounded-lg bg-white/[0.05] animate-pulse" />
        <div className="h-7 rounded-lg bg-white/[0.04] animate-pulse" />
      </div>
    </div>
    <BottomNav active={0} />
    {/* dynamic island */}
    <div className="absolute top-2 left-1/2 -translate-x-1/2 w-16 h-[18px] bg-black rounded-full" aria-hidden="true" />
  </motion.div>
);

// Android — the booking arrival-code surface (real OTP feature, no digits shown).
const AndroidScreen = ({ drift }) => (
  <motion.div style={{ y: drift }} className="absolute inset-0 bg-[#0B0E11] overflow-hidden will-change-transform">
    <div className="absolute top-9 inset-x-3.5">
      <p className="text-[0.5rem] uppercase tracking-[0.18em] text-zinc-500">Your booking</p>
      <p className="text-[0.68rem] font-semibold text-white mt-0.5">Arrival code</p>
    </div>
    <div className="absolute top-[26%] inset-x-3.5 rounded-xl bg-[#101316] border border-white/[0.08] p-3.5">
      <div className="flex justify-center gap-2">
        {[...Array(4)].map((_, i) => (
          <div key={i} className={`w-8 h-10 rounded-lg border flex items-center justify-center ${i === 0 ? "border-[#E50914]/60 bg-[#E50914]/[0.08]" : "border-white/10 bg-white/[0.03]"}`}>
            <span className="w-2.5 h-px bg-zinc-600" />
          </div>
        ))}
      </div>
      <p className="text-[0.5rem] text-zinc-500 text-center mt-2.5 leading-relaxed">Your host verifies this code on arrival.<br />You're only charged after verification.</p>
    </div>
    <div className="absolute top-[62%] inset-x-3.5 rounded-xl bg-[#101316] border border-white/[0.08] p-3 flex items-center gap-2.5">
      <div className="w-8 h-8 rounded-lg bg-white/[0.05] animate-pulse shrink-0" />
      <div className="flex-1 space-y-1.5">
        <div className="h-1.5 w-3/4 rounded bg-white/[0.07] animate-pulse" />
        <div className="h-1.5 w-1/2 rounded bg-white/[0.05] animate-pulse" />
      </div>
    </div>
    <BottomNav active={1} />
    {/* punch-hole camera */}
    <div className="absolute top-2.5 left-1/2 -translate-x-1/2 w-2.5 h-2.5 bg-black rounded-full ring-1 ring-white/[0.06]" aria-hidden="true" />
  </motion.div>
);

/* ---------- Phone chassis ---------- */

const Phone = ({ platform, children, style, labelTestId, testId }) => (
  <motion.div style={style} className="absolute left-1/2 top-1/2 will-change-transform" data-testid={testId}>
    <div className="-translate-x-1/2 -translate-y-1/2 flex flex-col items-center gap-5">
      <div
        className={`relative w-[224px] h-[458px] bg-[#050708] border border-white/[0.12] shadow-[0_40px_80px_-20px_rgba(0,0,0,0.85)] ${
          platform === "ios" ? "rounded-[44px] p-[10px]" : "rounded-[34px] p-[8px]"
        }`}
      >
        <div className={`relative w-full h-full overflow-hidden bg-[#0B0E11] ${platform === "ios" ? "rounded-[34px]" : "rounded-[26px]"}`}>
          {children}
        </div>
        {/* floor shadow / subtle reflection */}
        <div className="absolute -bottom-7 inset-x-6 h-7 bg-black/70 blur-xl rounded-full" aria-hidden="true" />
      </div>
      <p className="font-mono text-[0.62rem] font-semibold tracking-[0.3em] text-zinc-300 whitespace-nowrap" data-testid={labelTestId}>
        <span className="text-[#FF3B45]">{platform === "ios" ? "iOS" : "ANDROID"}</span>
        <span className="text-zinc-600"> — </span>COMING SOON
      </p>
    </div>
  </motion.div>
);

/* ---------- Section ---------- */

export default function MobileAppsComingSoon() {
  const reduced = useReducedMotion();
  const preset = usePreset();
  const trackRef = useRef(null);
  const { scrollYProgress: p } = useScroll({ target: trackRef, offset: ["start start", "end end"] });

  // Phones: approach → hold → separate. Counter-rotating the whole way.
  const iosX = useTransform(p, [0, 0.42, 0.78, 1], [-120, -170, -170, -preset.sep]);
  const andX = useTransform(p, [0, 0.42, 0.78, 1], [120, 170, 170, preset.sep]);
  const iosRot = useTransform(p, [0, 0.42, 1], [preset.rotIn, preset.rotMid, preset.rotOut]);
  const andRot = useTransform(p, [0, 0.42, 1], [-preset.rotIn, -preset.rotMid, -preset.rotOut]);
  const phoneZ = useTransform(p, [0, 0.42, 0.78, 1], [preset.zBack, 0, 0, preset.zFwd]);
  const phoneOpacity = useTransform(p, [0, 0.2, 0.9, 1], [0.62, 1, 1, 0.98]);
  const phoneY = useTransform(p, [0, 0.42, 1], [34, 0, -12]);
  // App screens drift at their own rate — parallax against the chassis.
  const screenDrift = useTransform(p, [0, 1], [preset.screenDrift, -preset.screenDrift]);
  // Logo floats between/above the phones with gentle parallax (visible from rest).
  const logoY = useTransform(p, [0, 0.5, 1], [10, -12, -20]);
  // Restrained red light sweeping through the composition.
  const glowX = useTransform(p, [0.3, 0.78], ["-38%", "38%"]);
  const glowOpacity = useTransform(p, [0.3, 0.5, 0.68, 0.82], [0, 0.55, 0.4, 0]);
  // Finale.
  const finaleOpacity = useTransform(p, [0.82, 0.95], [0, 1]);
  const finaleY = useTransform(p, [0.82, 1], [26, 0]);
  // Vignette keeps the headline readable mid-scroll, then yields so BOTH
  // phones read equally once they separate at the finale.
  const vignetteOpacity = useTransform(p, [0, 0.7, 0.92], [1, 1, 0.1]);

  const copy = (
    <>
      <p className="section-label justify-center" data-testid="mobile-apps-label">Mobile apps</p>
      <h2 id="mobile-apps-heading" data-testid="mobile-apps-headline"
          className="type-display mt-5 uppercase !leading-[1.04]">
        MyRedPark,<br /><span className="text-[#E50914]">in your pocket.</span>
      </h2>
      <p className="type-lede mt-6 max-w-md mx-auto" data-testid="mobile-apps-subcopy">
        Your parking marketplace is coming to mobile.<br />
        Find. Book. Park. Earn. — wherever you go.
      </p>
    </>
  );

  // Reduced motion / static composition — content never depends on animation.
  if (reduced) {
    return (
      <section className="relative py-24 sm:py-32 border-t border-[#141619] overflow-hidden" aria-labelledby="mobile-apps-heading" data-testid="section-mobile-apps">
        <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(60% 50% at 50% 45%, rgba(229,9,20,0.08), transparent 70%)" }} aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 text-center">
          <motion.div variants={fadeUp} {...revealOnce}>{copy}</motion.div>
          <motion.img variants={fadeUp} {...revealOnce} src="/brand/logo_dark.png" alt="MyRedPark" loading="lazy"
                      className="h-14 w-auto mx-auto mt-10 drop-shadow-[0_8px_30px_rgba(229,9,20,0.25)]" data-testid="mobile-apps-logo" />
          <motion.div variants={fadeUp} {...revealOnce} className="mt-12 flex flex-wrap items-start justify-center gap-10 sm:gap-16">
            <div className="scale-[0.72] sm:scale-90 -my-10">
              <div className="relative w-[224px] h-[458px]" data-testid="phone-ios">
                <div className="absolute inset-0 bg-[#050708] border border-white/[0.12] rounded-[44px] p-[10px] shadow-[0_40px_80px_-20px_rgba(0,0,0,0.85)]">
                  <div className="relative w-full h-full overflow-hidden bg-[#0B0E11] rounded-[34px]"><IosScreen drift={0} /></div>
                </div>
              </div>
              <p className="font-mono text-[0.62rem] font-semibold tracking-[0.3em] text-zinc-300 mt-6" data-testid="ios-label">
                <span className="text-[#FF3B45]">iOS</span><span className="text-zinc-600"> — </span>COMING SOON
              </p>
            </div>
            <div className="scale-[0.72] sm:scale-90 -my-10">
              <div className="relative w-[224px] h-[458px]" data-testid="phone-android">
                <div className="absolute inset-0 bg-[#050708] border border-white/[0.12] rounded-[34px] p-[8px] shadow-[0_40px_80px_-20px_rgba(0,0,0,0.85)]">
                  <div className="relative w-full h-full overflow-hidden bg-[#0B0E11] rounded-[26px]"><AndroidScreen drift={0} /></div>
                </div>
              </div>
              <p className="font-mono text-[0.62rem] font-semibold tracking-[0.3em] text-zinc-300 mt-6" data-testid="android-label">
                <span className="text-[#FF3B45]">ANDROID</span><span className="text-zinc-600"> — </span>COMING SOON
              </p>
            </div>
          </motion.div>
          <motion.p variants={fadeUp} {...revealOnce} className="mt-14 font-mono text-[0.72rem] font-semibold tracking-[0.34em] text-white" data-testid="mobile-apps-finale">
            THE APP IS ALMOST HERE.
          </motion.p>
        </div>
      </section>
    );
  }

  return (
    <section ref={trackRef} className="relative h-[340vh] border-t border-[#141619]" aria-labelledby="mobile-apps-heading" data-testid="section-mobile-apps">
      <div className="sticky top-0 h-[100svh] overflow-hidden flex items-center justify-center">
        {/* deep-charcoal backdrop with a restrained red ambience */}
        <div className="absolute inset-0" style={{ background: "radial-gradient(70% 60% at 50% 48%, #10141A 0%, #080B0D 70%)" }} aria-hidden="true" />
        <div className="absolute inset-0" style={{ background: "radial-gradient(52% 42% at 50% 52%, rgba(229,9,20,0.07), transparent 70%)" }} aria-hidden="true" />

        {/* cinematic red light sweep — transform/opacity only */}
        <motion.div aria-hidden="true"
                    style={{ x: glowX, y: "-50%", rotate: -14, opacity: glowOpacity,
                              background: "linear-gradient(90deg, transparent 0%, rgba(229,9,20,0.10) 30%, rgba(229,9,20,0.30) 50%, rgba(229,9,20,0.10) 70%, transparent 100%)" }}
                    className="absolute left-[-15%] w-[130%] h-[320px] top-1/2 blur-2xl will-change-transform pointer-events-none" />

        {/* 3D stage — fixed composition, CSS-scaled per breakpoint (no overflow) */}
        <div className="relative w-[920px] h-[600px] shrink-0 scale-[0.58] min-[430px]:scale-[0.64] sm:scale-[0.78] md:scale-90 lg:scale-100"
             style={{ perspective: "1200px" }}>
          {/* readability vignette behind the stable headline (fades for the finale) */}
          <motion.div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[560px] h-[380px] rounded-full z-[15] pointer-events-none"
               style={{ opacity: vignetteOpacity, background: "radial-gradient(closest-side, rgba(8,11,13,0.88), rgba(8,11,13,0.35) 60%, transparent)" }} aria-hidden="true" />

          {/* floating brand mark between/above the phones */}
          <motion.img src="/brand/logo_dark.png" alt="MyRedPark" loading="lazy" data-testid="mobile-apps-logo"
                      style={{ x: "-50%", y: logoY }}
                      className="absolute left-1/2 top-[2%] h-14 w-auto z-30 will-change-transform drop-shadow-[0_8px_30px_rgba(229,9,20,0.25)]" />

          {/* stable headline — does not move with the phones */}
          <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 z-20 text-center px-6">
            {copy}
          </div>

          {/* phones — start behind the copy, glide forward, then separate */}
          <div className="absolute inset-0 z-10" style={{ transformStyle: "preserve-3d" }}>
            <Phone platform="ios" testId="phone-ios" labelTestId="ios-label"
                   style={{ x: iosX, y: phoneY, z: phoneZ, rotateY: iosRot, opacity: phoneOpacity }}>
              <IosScreen drift={screenDrift} />
            </Phone>
            <Phone platform="android" testId="phone-android" labelTestId="android-label"
                   style={{ x: andX, y: phoneY, z: phoneZ, rotateY: andRot, opacity: phoneOpacity }}>
              <AndroidScreen drift={screenDrift} />
            </Phone>
          </div>

          {/* finale — revealed between the separated phones, below the stable headline */}
          <motion.div style={{ x: "-50%", y: finaleY, opacity: finaleOpacity }}
                      className="absolute left-1/2 top-[70%] z-30 pointer-events-none">
            <p className="font-mono text-[0.72rem] font-semibold tracking-[0.34em] text-white whitespace-nowrap bg-black/55 backdrop-blur-md border border-white/10 rounded-full px-6 py-3"
               data-testid="mobile-apps-finale">
              THE APP IS ALMOST HERE.
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
